import { useState, type ReactNode } from 'react'
import { IconeBuscar, IconeFiltro, IconeX } from './icones'
import { Botao, juntar } from './ui'

/**
 * As peças que montam uma página: o topo, a barra de filtros, o seletor de
 * período e o texto de tela vazia.
 *
 * Existem porque cada página ia sendo feita do seu jeito: Leads e Contatos
 * sem título, o Painel com título maior, Anúncios com os filtros
 * num cartão sempre aberto, as Estatísticas com eles na linha do título, e o
 * "7, 30, 90 dias" em dois desenhos na mesma tela. Com as peças aqui, a
 * página nova sai igual às outras sem ninguém precisar lembrar de como era.
 */

/** Campo de 36px das barras de filtro: busca, lista, data. */
export const CLASSE_DO_CAMPO =
  'h-9 rounded-lg bg-superficie px-3 text-sm text-tinta-900 ring-1 ring-tinta-200 outline-none transition hover:ring-tinta-300 focus:ring-2 focus:ring-marca/60'

/**
 * O rótulo pequeno do sistema, em maiúsculas cinza: nome do número em
 * destaque, título de grupo dentro de um cartão.
 */
export const CLASSE_DO_ROTULO = 'text-xs font-semibold uppercase tracking-wider text-tinta-400'

/**
 * A linha de cabeçalho das tabelas, no mesmo desenho do rótulo pequeno. Vai
 * no `<tr>`; cada `<th>` só cuida do próprio espaçamento.
 */
/**
 * A tabela da Configuração (reforma de 05/10/2026). Com o menu lateral a
 * coluna de conteúdo ficou mais estreita, e as colunas sem respiro encostavam
 * umas nas outras ("fulano@empresa.comComercial"). O espaço vai à direita de
 * cada célula, menos da última, que é a das ações.
 */
export const CLASSE_DA_TABELA =
  'w-full text-sm [&_td]:pr-4 [&_th]:pr-4 [&_td:last-child]:pr-0 [&_th:last-child]:pr-0'

export const CLASSE_DO_CABECALHO_DA_TABELA =
  'border-b border-tinta-200 text-left text-xs font-semibold uppercase tracking-wider text-tinta-400'

/**
 * O topo de toda página: o nome, uma frase do que se faz nela, e a ação
 * principal à direita ("Novo contato", "Nova tarefa").
 */
export function CabecalhoDaPagina({
  titulo,
  descricao,
  acoes,
  className,
}: {
  titulo: string
  descricao?: ReactNode
  acoes?: ReactNode
  className?: string
}) {
  return (
    <header className={juntar('flex flex-wrap items-end justify-between gap-3', className)}>
      <div className="min-w-0 flex-1">
        <h1 className="text-2xl font-bold text-tinta-900">{titulo}</h1>
        {descricao ? <p className="mt-1 text-sm text-tinta-500">{descricao}</p> : null}
      </div>
      {acoes ? <div className="flex shrink-0 flex-wrap items-center gap-2">{acoes}</div> : null}
    </header>
  )
}

/**
 * A barra de filtros de toda página: busca, o filtro que se usa toda vez, e
 * o botão Filtros com o resto.
 *
 * O que fica escondido no botão aparece contado nele: filtro ligado e
 * esquecido é o jeito mais fácil de a lista parecer vazia sem motivo.
 */
export function BarraDeFiltros({
  busca,
  children,
  filtros,
  ligados = 0,
  aoLimpar,
  direita,
  embutida = false,
  className,
}: {
  busca?: {
    valor: string
    aoMudar: (valor: string) => void
    placeholder: string
    /** Nome do campo para leitor de tela. */
    rotulo: string
    /** Classe de largura; o padrão cabe "Buscar por nome". */
    largura?: string
  }
  /** O que fica à vista depois da busca: o filtro principal da página. */
  children?: ReactNode
  /** O conteúdo do painel do botão Filtros. Sem ele, não há botão. */
  filtros?: ReactNode
  /** Quantos filtros estão ligados, para o número no botão. */
  ligados?: number
  /** "Limpar filtros", no pé do painel, quando há algum ligado. */
  aoLimpar?: () => void
  /** O que encosta na direita da barra: resumo, alternâncias de visão. */
  direita?: ReactNode
  /**
   * Dentro de um cartão: sem fundo nem sombra próprios, que cartão dentro
   * de cartão vira moldura em cima de moldura.
   */
  embutida?: boolean
  className?: string
}) {
  const [aberto, setAberto] = useState(false)

  return (
    <div
      className={juntar(!embutida && 'rounded-2xl bg-superficie shadow-suave ring-1 ring-tinta-200/60', className)}
    >
      <div className={juntar('flex flex-wrap items-center gap-2', !embutida && 'p-2.5')}>
        {busca ? (
          // `max-w-full`: no celular a largura fixa (320px em Contatos) passava da borda do cartão.
          <label className="relative min-w-0 max-w-full">
            <span className="sr-only">{busca.rotulo}</span>
            <IconeBuscar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-400" />
            <input
              value={busca.valor}
              onChange={(e) => busca.aoMudar(e.target.value)}
              placeholder={busca.placeholder}
              className={juntar(
                CLASSE_DO_CAMPO,
                busca.largura ?? 'w-64',
                'max-w-full rounded-full pl-9',
              )}
            />
          </label>
        ) : null}

        {children}

        {filtros ? (
          <button
            type="button"
            onClick={() => setAberto((a) => !a)}
            aria-expanded={aberto}
            className={juntar(
              'flex h-9 items-center gap-1.5 rounded-full px-4 text-sm font-semibold transition',
              aberto || ligados > 0
                ? 'bg-marca/10 text-marca ring-1 ring-marca/30'
                : 'bg-superficie text-tinta-700 shadow-suave ring-1 ring-tinta-200 hover:bg-tinta-50',
            )}
          >
            <IconeFiltro className="h-4 w-4" />
            Filtros
            {ligados > 0 ? (
              <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-marca px-1 text-[11px] font-medium tabular-nums text-white">
                {ligados}
              </span>
            ) : null}
          </button>
        ) : null}

        {direita ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">{direita}</div>
        ) : null}
      </div>

      {filtros && aberto ? (
        <div
          className={juntar(
            'space-y-3',
            embutida ? 'mt-3 rounded-xl bg-tinta-50 p-3' : 'border-t border-tinta-100 p-4',
          )}
        >
          {filtros}
          {ligados > 0 && aoLimpar ? (
            <Botao variante="texto" onClick={aoLimpar}>
              <IconeX className="h-3.5 w-3.5" />
              Limpar filtros
            </Botao>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** A grade do painel de filtros: duas colunas no meio, quatro na tela larga. */
export function GradeDeFiltros({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
}

/** Um campo do painel de filtros, com o nome em cima. */
export function CampoDeFiltro({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-tinta-700">{rotulo}</span>
      {children}
    </label>
  )
}

/**
 * Escolha de uma opção só entre poucas, lado a lado: a marcada sobe em
 * branco. É o desenho do período ("7 dias", "30 dias") e das partes de uma
 * aba (QR code ou API oficial, em Números).
 */
export function SeletorSegmentado<T extends string | number>({
  opcoes,
  valor,
  aoEscolher,
  rotulo,
  className,
}: {
  opcoes: { valor: T; rotulo: string }[]
  /** `null` quando o período na tela não é nenhum dos atalhos. */
  valor: T | null
  aoEscolher: (valor: T) => void
  rotulo: string
  className?: string
}) {
  return (
    <div
      role="group"
      aria-label={rotulo}
      className={juntar('flex flex-wrap gap-0.5 rounded-full bg-tinta-100 p-1 text-sm', className)}
    >
      {opcoes.map((o) => (
        <button
          key={String(o.valor)}
          type="button"
          aria-pressed={o.valor === valor}
          onClick={() => aoEscolher(o.valor)}
          className={juntar(
            'rounded-full px-3 py-1 font-semibold leading-5 transition',
            o.valor === valor
              ? 'bg-superficie text-tinta-900 shadow-suave'
              : 'text-tinta-500 hover:text-tinta-900',
          )}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  )
}

/**
 * O que aparece onde não há nada: uma frase curta, cinza, que diz o que
 * viria ali. "Vazio" sozinho não diz se é filtro, erro ou falta de dado.
 */
export function Vazio({ children, compacto = false }: { children: ReactNode; compacto?: boolean }) {
  return (
    <p
      className={juntar(
        'text-center text-tinta-400',
        compacto ? 'px-3 py-6 text-xs' : 'py-8 text-sm',
      )}
    >
      {children}
    </p>
  )
}

/** O período por atalho: o mesmo seletor, com o nome de quem usa. */
export const SeletorDePeriodo = SeletorSegmentado

/**
 * Fileira de abas de uma página, no desenho das abas da Configuração: pastilha
 * na aba aberta, e quebra em duas linhas em vez de rolar de lado.
 */
export function BarraDeAbas<T extends string>({
  abas,
  valor,
  aoEscolher,
  rotulo,
  className,
}: {
  abas: { id: T; rotulo: string }[]
  valor: T
  aoEscolher: (id: T) => void
  /** Nome da fileira para leitor de tela ("Seções das estatísticas"). */
  rotulo: string
  className?: string
}) {
  return (
    <nav
      role="tablist"
      aria-label={rotulo}
      className={juntar('flex flex-wrap gap-1.5 border-b border-tinta-200 pb-2', className)}
    >
      {abas.map((a) => (
        <button
          key={a.id}
          type="button"
          role="tab"
          aria-selected={valor === a.id}
          onClick={() => aoEscolher(a.id)}
          className={juntar(
            'shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition',
            valor === a.id
              ? 'bg-marca/10 font-semibold text-marca ring-1 ring-marca/25'
              : 'font-medium text-tinta-500 hover:bg-tinta-100 hover:text-tinta-900',
          )}
        >
          {a.rotulo}
        </button>
      ))}
    </nav>
  )
}
